import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { HiOutlineXMark } from "react-icons/hi2";
import "./ClientActionModal.scss";

export default function ClientActionModal({
  title,
  children,
  open = false,
  close,
  size = 2,
}) {
  const overlayRef = useRef();
  const modalRef = useRef();

  useEffect(() => {
    if (open) {
      modalRef.current?.classList.add("modalOpen");
      overlayRef.current?.focus();
      document.body.classList.add("overflow-hidden");
      return () => {
        document.body.classList.remove("overflow-hidden");
      };
    }
  }, [open]);

  const handleCloseModalAnimate = () => {
    overlayRef.current?.classList.add("modalOverlayClose");
    modalRef.current?.classList.add("modalClose");
    modalRef.current?.classList.remove("modalOpen");

    setTimeout(() => {
      close(false);
    }, 260);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      handleCloseModalAnimate();
    }
  };

  const getSize = () => {
    switch (size) {
      case "xxxSmall":
        return "max-w-md";
      case "xxSmall":
        return "max-w-lg";
      case "xSmall":
        return "max-w-xl";
      case "small":
        return "max-w-3xl";
      case "medium":
        return "max-w-5xl";
      case "large":
        return "max-w-7xl";
      default:
        return "max-w-3xl";
    }
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      onKeyDown={handleKeyDown}
      ref={overlayRef}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      className="show-info-modal fixed inset-0 z-[70000] bg-[rgba(34,14,0,0.68)] p-4 backdrop-blur-sm md:p-5"
    >
      <div className="flex min-h-full items-center justify-center">
        <div ref={modalRef} className={`relative w-full ${getSize()}`}>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/20 bg-[linear-gradient(180deg,rgba(255,251,246,0.94),rgba(255,237,214,0.88))] shadow-[0_28px_90px_rgba(42,17,0,0.42)] backdrop-blur-2xl dark:bg-[linear-gradient(180deg,rgba(61,27,0,0.96),rgba(34,14,0,0.92))]">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_top,rgba(255,178,91,0.24),transparent_68%)]" />

            <div className="relative flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4 md:px-6">
              <div className="min-w-0">
                <div className="mb-1 h-1.5 w-14 rounded-full bg-[linear-gradient(90deg,#FFB25B,#FF8B1F)]" />
                <h3
                  className="truncate text-base font-bold md:text-lg"
                  style={{ color: "var(--layout-text)" }}
                >
                  {title}
                </h3>
              </div>

              <button
                type="button"
                className="portal-outline-button inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl p-2 text-2xl"
                onClick={handleCloseModalAnimate}
              >
                <HiOutlineXMark />
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            <div
              className="body-modal relative overflow-auto px-4 py-4 md:px-6 md:py-5"
              style={{ maxHeight: "min(78vh, 860px)" }}
            >
              {children}
            </div>

            <div className="relative flex items-center justify-end border-t border-white/10 px-4 py-3 md:px-6">
              <button
                type="button"
                onClick={handleCloseModalAnimate}
                className="portal-outline-button min-w-24 rounded-xl px-4 py-2"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
